import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { TestAffineEditorContainer } from '@blocksuite/integration-test';
import { deserializeXYWH } from '@blocksuite/global/gfx';
import { StrokeStyle } from '@blocksuite/affine-model';

@customElement('properties-panel')
export class PropertiesPanel extends LitElement {
  static override styles = css`
    :host {
      display: none;
      position: fixed;
      right: 0;
      top: 0;
      width: 320px;
      height: 100vh;
      background-color: var(--panel-bg, #ffffff);
      border-left: 1px solid var(--panel-border, #e3e3e3);
      box-shadow: -4px 0 20px rgba(0, 0, 0, 0.05);
      font-family: var(--affine-font-family, -apple-system, BlinkMacSystemFont, sans-serif);
      color: var(--text-main, #1e1e24);
      z-index: 10000;
      box-sizing: border-box;
      overflow-y: auto;
    }

    /* Light Theme (default) */
    :host {
      --panel-bg: #ffffff;
      --panel-border: #e8e8ec;
      --input-bg: #f4f4f6;
      --accent-color: #0c8ce9;
      --accent-hover: #0a70ba;
      --text-main: #1c1c1f;
      --text-sub: #767680;
      --border-radius-lg: 12px;
      --border-radius-md: 8px;
      --border-radius-sm: 4px;
    }

    /* Dark Theme */
    :host-context(html.dark),
    :host-context([data-theme='dark']) {
      --panel-bg: #121214;
      --panel-border: #222226;
      --input-bg: #1e1e22;
      --accent-color: #0c8ce9;
      --accent-hover: #1e96eb;
      --text-main: #f5f5f7;
      --text-sub: #8e8e93;
    }

    .properties-wrapper {
      background: var(--panel-bg);
      color: var(--text-main);
      min-height: 100%;
      padding-bottom: 40px;
    }

    /* Header Styling */
    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;
      border-bottom: 1px solid var(--panel-border);
      background: var(--panel-bg);
      position: sticky;
      top: 0;
      z-index: 10;
    }

    .panel-title {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-main);
      text-transform: uppercase;
      letter-spacing: 0.8px;
    }

    .panel-close-btn {
      background: transparent;
      border: none;
      color: var(--text-sub);
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 6px;
      border-radius: var(--border-radius-md);
      transition: all 0.2s;
    }

    .panel-close-btn:hover {
      background: var(--input-bg);
      color: var(--text-main);
    }

    .panel-section {
      padding: 16px;
      border-bottom: 1px solid var(--panel-border);
    }

    .section-title {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      color: var(--text-sub);
      margin-bottom: 12px;
      letter-spacing: 0.5px;
    }

    /* Selection Type Row */
    .frame-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .frame-select {
      font-weight: 600;
      font-size: 13px;
      border: 1px solid var(--panel-border);
      background: var(--input-bg);
      padding: 6px 12px;
      border-radius: var(--border-radius-md);
      cursor: pointer;
      color: var(--text-main);
      outline: none;
      transition: all 0.15s;
    }
    
    .frame-select:hover {
      background: var(--panel-bg);
      border-color: var(--accent-color);
    }

    .frame-icons {
      display: flex;
      gap: 12px;
      color: var(--text-sub);
    }

    .frame-icon-btn {
      background: transparent;
      border: none;
      cursor: pointer;
      color: inherit;
      display: flex;
      align-items: center;
      padding: 4px;
      border-radius: var(--border-radius-sm);
    }

    .frame-icon-btn:hover {
      color: var(--text-main);
      background: var(--input-bg);
    }

    /* Grid layout */
    .property-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }

    .property-item {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .property-item.full-width {
      grid-column: span 2;
    }

    .property-item.row-layout {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    label {
      font-size: 11px;
      font-weight: 600;
      color: var(--text-sub);
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .input-prefix {
      position: absolute;
      left: 10px;
      font-size: 11px;
      font-weight: 600;
      color: var(--text-sub);
      pointer-events: none;
    }

    .input-field {
      width: 100%;
      background: var(--input-bg);
      border: 1px solid transparent;
      border-radius: var(--border-radius-md);
      padding: 8px 10px;
      padding-left: 24px;
      font-size: 12px;
      color: var(--text-main);
      outline: none;
      transition: all 0.15s;
      box-sizing: border-box;
    }

    .input-field:focus {
      border-color: var(--accent-color);
      background: var(--panel-bg);
      box-shadow: 0 0 0 2px rgba(12, 140, 233, 0.15);
    }

    /* Alignment Panel */
    .alignment-bar {
      display: flex;
      justify-content: space-between;
      background: var(--input-bg);
      border-radius: var(--border-radius-md);
      padding: 3px;
    }

    .alignment-btn {
      flex: 1;
      border: none;
      background: transparent;
      padding: 8px;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: var(--border-radius-sm);
      color: var(--text-sub);
      transition: all 0.15s;
    }

    .alignment-btn:hover {
      background: rgba(0, 0, 0, 0.05);
      color: var(--text-main);
    }

    .alignment-btn.active {
      background: var(--panel-bg);
      color: var(--accent-color);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .align-icon {
      width: 14px;
      height: 14px;
      fill: currentColor;
    }

    /* Swatches */
    .color-swatch-list {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      margin-top: 6px;
    }

    .color-swatch {
      width: 22px;
      height: 22px;
      border-radius: var(--border-radius-sm);
      cursor: pointer;
      border: 1.5px solid transparent;
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
      transition: transform 0.15s;
    }

    .color-swatch:hover {
      transform: scale(1.15);
    }

    .color-swatch.active {
      border-color: var(--accent-color);
      transform: scale(1.1);
    }

    /* Action Buttons */
    .btn-group {
      display: flex;
      gap: 8px;
      margin-top: 10px;
    }

    .btn-action {
      flex: 1;
      border: 1px solid var(--panel-border);
      background: var(--panel-bg);
      border-radius: var(--border-radius-md);
      padding: 8px;
      font-size: 12px;
      font-weight: 500;
      color: var(--text-main);
      cursor: pointer;
      transition: all 0.15s;
    }

    .btn-action:hover {
      background: var(--input-bg);
    }

    .btn-action.primary {
      background: var(--accent-color);
      color: white;
      border-color: var(--accent-color);
    }

    .btn-action.primary:hover {
      background: var(--accent-hover);
    }

    .empty-state {
      padding: 40px 20px;
      text-align: center;
      color: var(--text-sub);
      font-size: 12px;
      line-height: 1.5;
    }

    /* Gradient Stop Card Styling */
    .gradient-stop-item {
      border: 1px solid var(--panel-border);
      border-radius: var(--border-radius-md);
      padding: 10px;
      margin-top: 8px;
      background: var(--input-bg);
    }

    .stop-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
    }

    .stop-title {
      font-size: 11px;
      font-weight: 600;
      color: var(--text-main);
    }

    .remove-stop-btn {
      background: transparent;
      border: none;
      color: #ef5350;
      cursor: pointer;
      font-size: 11px;
      font-weight: 500;
    }

    .remove-stop-btn:hover {
      text-decoration: underline;
    }
  `;

  @property({ attribute: false })
  accessor editor!: TestAffineEditorContainer;

  @state()
  private accessor _selectedElements: any[] = [];

  private _disposables: (() => void)[] = [];

  override connectedCallback() {
    super.connectedCallback();
    this._setupListeners();
    this._setupScrubbing();
  }

  private _setupScrubbing() {
    this.addEventListener('mousedown', (e: MouseEvent) => {
      const target = e.composedPath()[0] as HTMLElement;
      if (target && target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'number') {
        const input = target as HTMLInputElement;
        const initialX = e.clientX;
        const initialValue = parseFloat(input.value) || 0;
        const step = parseFloat(input.step) || 1;
        const min = input.min !== '' ? parseFloat(input.min) : -Infinity;
        const max = input.max !== '' ? parseFloat(input.max) : Infinity;

        let hasDragged = false;

        const onMouseMove = (moveEvent: MouseEvent) => {
          const deltaX = moveEvent.clientX - initialX;
          if (Math.abs(deltaX) > 2) {
            hasDragged = true;
          }

          if (hasDragged) {
            moveEvent.preventDefault();
            // 2 pixels of mouse movement translates to 1 step unit
            const units = Math.round(deltaX / 2);
            let newValue = initialValue + units * step;
            if (newValue < min) newValue = min;
            if (newValue > max) newValue = max;

            // Update input display and trigger change callback
            input.value = String(newValue);
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        };

        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      }
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._disposables.forEach(d => d());
    this._disposables = [];
  }

  private _setupListeners() {
    if (!this.editor) return;

    const gfx = this.editor.std.get(GfxControllerIdentifier);
    if (gfx) {
      const updateSelection = () => {
        this._selectedElements = [...gfx.selection.selectedElements];
        this.requestUpdate();
      };

      const disposable = gfx.selection.slots.updated.subscribe(updateSelection);
      this._disposables.push(() => disposable.unsubscribe());

      updateSelection();
    }
  }

  toggle(visible?: boolean) {
    const isVisible = visible !== undefined ? visible : this.style.display !== 'block';
    this.style.display = isVisible ? 'block' : 'none';
    if (isVisible) {
      this._setupListeners();
    }
    this.dispatchEvent(new CustomEvent('properties-panel-toggle', {
      bubbles: true,
      composed: true,
      detail: { visible: isVisible }
    }));
  }

  private _updateElementProperty(propertyKey: string, value: any) {
    const gfx = this.editor.std.get(GfxControllerIdentifier);
    if (!gfx) return;

    let finalValue = value;
    if ((propertyKey === 'fillColor' || propertyKey === 'color') && typeof value === 'string' && value.startsWith('{')) {
      try {
        const grad = JSON.parse(value);
        if (grad.scope === 'overall') {
          const minX = Math.min(...this._selectedElements.map(el => el.xywh ? deserializeXYWH(el.xywh)[0] : 0));
          const minY = Math.min(...this._selectedElements.map(el => el.xywh ? deserializeXYWH(el.xywh)[1] : 0));
          const maxX = Math.max(...this._selectedElements.map(el => el.xywh ? (deserializeXYWH(el.xywh)[0] + deserializeXYWH(el.xywh)[2]) : 0));
          const maxY = Math.max(...this._selectedElements.map(el => el.xywh ? (deserializeXYWH(el.xywh)[1] + deserializeXYWH(el.xywh)[3]) : 0));
          grad.bounds = { minX, minY, maxX, maxY };
          finalValue = JSON.stringify(grad);
        }
      } catch (e) {}
    }

    this._selectedElements.forEach(el => {
      gfx.updateElement(el.id, { [propertyKey]: finalValue });
      // Mutate local property so panel inputs show the updated value instantly
      el[propertyKey] = finalValue;
    });
    this.requestUpdate();

    // Trigger instant canvas re-render
    if (this.editor) {
      this.editor.requestUpdate();
      try {
        this.editor.std?.host?.requestUpdate();
      } catch (_) {}
    }
  }

  private _handleAlign(alignment: string) {
    const gfx = this.editor.std.get(GfxControllerIdentifier);
    if (!gfx || this._selectedElements.length < 2) return;

    const bounds = this._selectedElements.map(el => {
      const [x, y, w, h] = deserializeXYWH(el.xywh);
      return { id: el.id, x, y, w, h };
    });

    if (alignment === 'left') {
      const minX = Math.min(...bounds.map(b => b.x));
      bounds.forEach(b => {
        const val = `[${minX},${b.y},${b.w},${b.h}]`;
        gfx.updateElement(b.id, { xywh: val });
        const target = this._selectedElements.find(el => el.id === b.id);
        if (target) target.xywh = val;
      });
    } else if (alignment === 'center') {
      const centers = bounds.map(b => b.x + b.w / 2);
      const avgCenter = centers.reduce((a, b) => a + b, 0) / centers.length;
      bounds.forEach(b => {
        const val = `[${avgCenter - b.w / 2},${b.y},${b.w},${b.h}]`;
        gfx.updateElement(b.id, { xywh: val });
        const target = this._selectedElements.find(el => el.id === b.id);
        if (target) target.xywh = val;
      });
    } else if (alignment === 'right') {
      const maxX = Math.max(...bounds.map(b => b.x + b.w));
      bounds.forEach(b => {
        const val = `[${maxX - b.w},${b.y},${b.w},${b.h}]`;
        gfx.updateElement(b.id, { xywh: val });
        const target = this._selectedElements.find(el => el.id === b.id);
        if (target) target.xywh = val;
      });
    } else if (alignment === 'top') {
      const minY = Math.min(...bounds.map(b => b.y));
      bounds.forEach(b => {
        const val = `[${b.x},${minY},${b.w},${b.h}]`;
        gfx.updateElement(b.id, { xywh: val });
        const target = this._selectedElements.find(el => el.id === b.id);
        if (target) target.xywh = val;
      });
    } else if (alignment === 'middle') {
      const middles = bounds.map(b => b.y + b.h / 2);
      const avgMiddle = middles.reduce((a, b) => a + b, 0) / middles.length;
      bounds.forEach(b => {
        const val = `[${b.x},${avgMiddle - b.h / 2},${b.w},${b.h}]`;
        gfx.updateElement(b.id, { xywh: val });
        const target = this._selectedElements.find(el => el.id === b.id);
        if (target) target.xywh = val;
      });
    } else if (alignment === 'bottom') {
      const maxY = Math.max(...bounds.map(b => b.y + b.h));
      bounds.forEach(b => {
        const val = `[${b.x},${maxY - b.h},${b.w},${b.h}]`;
        gfx.updateElement(b.id, { xywh: val });
        const target = this._selectedElements.find(el => el.id === b.id);
        if (target) target.xywh = val;
      });
    }
    this.requestUpdate();

    // Trigger instant canvas re-render
    if (this.editor) {
      this.editor.requestUpdate();
      try {
        this.editor.std?.host?.requestUpdate();
      } catch (_) {}
    }
  }

  // Gradient Helper Methods
  private _isGradient(fillColor: string) {
    if (!fillColor || typeof fillColor !== 'string') return false;
    return fillColor.trim().startsWith('{');
  }

  private _getGradientData(fillColor: string) {
    try {
      if (this._isGradient(fillColor)) {
        return JSON.parse(fillColor);
      }
    } catch { }
    return {
      type: 'linear',
      angle: 90,
      stops: [
        { offset: 0, color: 'rgba(12, 140, 233, 1)' },
        { offset: 1, color: 'rgba(239, 83, 80, 1)' }
      ]
    };
  }

  private _hexToRgba(hex: string, alpha = 1) {
    hex = hex.replace('#', '');
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private _parseColorAndAlpha(colorStr: string) {
    if (!colorStr) return { hex: '#000000', alpha: 1 };
    if (colorStr.startsWith('rgba') || colorStr.startsWith('rgb')) {
      const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const a = match[4] !== undefined ? parseFloat(match[4]) : 1;
        const hex = '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
        return { hex, alpha: a };
      }
    }
    return { hex: colorStr.startsWith('#') ? colorStr : '#000000', alpha: 1 };
  }

  // ─── SEGREGATED RENDERING BLOCKS ───────────────────────────────────────────

  private _renderPositionSection(el: any) {
    let x = 0, y = 0, w = 100, h = 100;
    if (el.xywh) {
      const [mx, my, mw, mh] = deserializeXYWH(el.xywh);
      x = Math.round(mx);
      y = Math.round(my);
      w = Math.round(mw);
      h = Math.round(mh);
    }
    const rotate = el.rotate || 0;

    return html`
      <div class="panel-section">
        <div class="section-title">Position</div>
        <div class="property-grid">
          <div class="property-item">
            <div class="input-wrapper">
              <span class="input-prefix">X</span>
              <input type="number" class="input-field" .value="${x}" @input="${(e: any) => {
                const nx = parseFloat(e.target.value) || 0;
                this._updateElementProperty('xywh', `[${nx},${y},${w},${h}]`);
              }}" />
            </div>
          </div>
          <div class="property-item">
            <div class="input-wrapper">
              <span class="input-prefix">Y</span>
              <input type="number" class="input-field" .value="${y}" @input="${(e: any) => {
                const ny = parseFloat(e.target.value) || 0;
                this._updateElementProperty('xywh', `[${x},${ny},${w},${h}]`);
              }}" />
            </div>
          </div>
          <div class="property-item">
            <div class="input-wrapper">
              <span class="input-prefix">R</span>
              <input type="number" class="input-field" .value="${rotate}" @input="${(e: any) => {
                this._updateElementProperty('rotate', parseFloat(e.target.value) || 0);
              }}" />
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderLayoutSection(el: any) {
    let x = 0, y = 0, w = 100, h = 100;
    if (el.xywh) {
      const [mx, my, mw, mh] = deserializeXYWH(el.xywh);
      x = Math.round(mx);
      y = Math.round(my);
      w = Math.round(mw);
      h = Math.round(mh);
    }

    return html`
      <div class="panel-section">
        <div class="section-title">Layout</div>
        <div class="property-grid">
          <div class="property-item">
            <div class="input-wrapper">
              <span class="input-prefix">W</span>
              <input type="number" class="input-field" .value="${w}" @input="${(e: any) => {
                const nw = Math.max(1, parseFloat(e.target.value) || 1);
                this._updateElementProperty('xywh', `[${x},${y},${nw},${h}]`);
              }}" />
            </div>
          </div>
          <div class="property-item">
            <div class="input-wrapper">
              <span class="input-prefix">H</span>
              <input type="number" class="input-field" .value="${h}" @input="${(e: any) => {
                const nh = Math.max(1, parseFloat(e.target.value) || 1);
                this._updateElementProperty('xywh', `[${x},${y},${w},${nh}]`);
              }}" />
            </div>
          </div>
          <div class="property-item full-width row-layout">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="checkbox" ?checked="${el.clipContent}" @change="${(e: any) => this._updateElementProperty('clipContent', e.target.checked)}" />
              Clip content
            </label>
          </div>
        </div>
      </div>
    `;
  }

  private _renderAppearanceSection(el: any) {
    const opacity = el.opacity !== undefined ? Math.round(el.opacity * 100) : 100;
    const radius = el.radius || 0;

    return html`
      <div class="panel-section">
        <div class="section-title">Appearance</div>
        <div class="property-grid">
          <div class="property-item">
            <label>Opacity</label>
            <div class="input-wrapper">
              <input type="number" class="input-field" style="padding-left: 10px;" min="0" max="100" .value="${opacity}" @input="${(e: any) => {
                this._updateElementProperty('opacity', parseFloat(e.target.value) / 100);
              }}" />
            </div>
          </div>
          <div class="property-item" style="grid-column: span 2;">
            <label>Corner Radius (${radius})</label>
            <div style="display: flex; gap: 8px; align-items: center; width: 100%;">
              <input type="range" style="flex: 1;" min="0" max="100" step="1" .value="${radius}" @input="${(e: any) => {
                this._updateElementProperty('radius', parseFloat(e.target.value) || 0);
              }}" />
              <input type="number" class="input-field" style="padding-left: 10px; width: 60px; box-sizing: border-box;" min="0" .value="${radius}" @input="${(e: any) => {
                this._updateElementProperty('radius', parseFloat(e.target.value) || 0);
              }}" />
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderColorOrGradientSection(el: any, propertyKey: string, title: string, colors: string[]) {
    const propertyValue = el[propertyKey] || (propertyKey === 'color' ? '#000000' : '#ffffff');
    const isGrad = this._isGradient(propertyValue);
    const grad = this._getGradientData(propertyValue);

    return html`
      <div class="panel-section">
        <div class="section-title">${title}</div>
        <div class="property-grid" style="margin-bottom: 12px;">
          <div class="property-item full-width">
            <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${isGrad ? 'gradient' : 'solid'}" @change="${(e: any) => {
              if (e.target.value === 'gradient') {
                this._updateElementProperty(propertyKey, JSON.stringify(grad));
              } else {
                this._updateElementProperty(propertyKey, propertyKey === 'color' ? '#000000' : '#29b6f6');
              }
            }}">
              <option value="solid">Solid Fill</option>
              <option value="gradient">Gradient Fill</option>
            </select>
          </div>
        </div>

        ${!isGrad ? html`
          <!-- Solid Fill -->
          <div class="property-grid" style="margin-bottom: 12px;">
            <div class="property-item full-width">
              <div class="input-wrapper" style="display: flex; gap: 8px; align-items: center;">
                <input type="color" .value="${propertyValue}" @input="${(e: any) => this._updateElementProperty(propertyKey, e.target.value)}" style="width: 32px; height: 32px; border-radius: var(--border-radius-md); border: 1px solid var(--panel-border); cursor: pointer; padding: 0;" />
                <input type="text" class="input-field" style="padding-left: 10px; height: 32px;" .value="${propertyValue}" @input="${(e: any) => this._updateElementProperty(propertyKey, e.target.value)}" />
              </div>
            </div>
          </div>
          <div class="color-swatch-list">
            ${colors.map(color => html`
              <div 
                class="color-swatch ${propertyValue === color ? 'active' : ''}" 
                style="background-color: ${color};"
                @click="${() => this._updateElementProperty(propertyKey, color)}"
              ></div>
            `)}
          </div>
        ` : html`
          <!-- Gradient Fill Editor -->
          <div class="property-grid" style="margin-bottom: 12px;">
            <div class="property-item full-width">
              <label>Gradient Scope</label>
              <div style="display: flex; gap: 8px; margin-top: 4px;">
                <button class="btn-action ${(!grad.scope || grad.scope === 'individual') ? 'primary' : ''}" 
                  style="flex: 1; font-size: 11px; border-radius: var(--border-radius-md);" 
                  @click="${() => {
                    grad.scope = 'individual';
                    this._updateElementProperty(propertyKey, JSON.stringify(grad));
                  }}">
                  Individual
                </button>
                <button class="btn-action ${(grad.scope === 'overall') ? 'primary' : ''}" 
                  style="flex: 1; font-size: 11px; border-radius: var(--border-radius-md);" 
                  @click="${() => {
                    grad.scope = 'overall';
                    this._updateElementProperty(propertyKey, JSON.stringify(grad));
                  }}">
                  Overall
                </button>
              </div>
            </div>
          </div>

          <div class="property-grid" style="margin-bottom: 12px;">
            <div class="property-item">
              <label>Gradient Type</label>
              <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${grad.type}" @change="${(e: any) => {
                grad.type = e.target.value;
                this._updateElementProperty(propertyKey, JSON.stringify(grad));
              }}">
                <option value="linear">Linear</option>
                <option value="radial">Radial</option>
              </select>
            </div>
            ${grad.type === 'linear' ? html`
              <div class="property-item">
                <label>Angle (${grad.angle || 0}°)</label>
                <input type="range" min="0" max="360" .value="${grad.angle || 0}" @input="${(e: any) => {
                  grad.angle = parseInt(e.target.value);
                  this._updateElementProperty(propertyKey, JSON.stringify(grad));
                }}" />
              </div>
            ` : nothing}
          </div>

          <!-- Color Stops -->
          <div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 10px;">
              <span style="font-size: 11px; font-weight: 600; color: var(--text-sub);">GRADIENT STOPS</span>
              <button class="btn-action" style="padding: 2px 6px; font-size: 10px; width: auto; border-radius: var(--border-radius-sm);" @click="${() => {
                grad.stops.reverse();
                grad.stops = grad.stops.map((stop: any) => ({
                  ...stop,
                  offset: 1 - stop.offset
                }));
                grad.stops.sort((a: any, b: any) => a.offset - b.offset);
                this._updateElementProperty(propertyKey, JSON.stringify(grad));
              }}">Reverse Order</button>
            </div>

            ${grad.stops.map((stop: any, index: number) => {
              const { hex, alpha } = this._parseColorAndAlpha(stop.color);
              return html`
                <div class="gradient-stop-item" style="border-radius: var(--border-radius-md);">
                  <div class="stop-header">
                    <span class="stop-title">Stop ${index + 1} (${Math.round(stop.offset * 100)}%)</span>
                    ${grad.stops.length > 2 ? html`
                      <button class="remove-stop-btn" @click="${() => {
                        grad.stops.splice(index, 1);
                        this._updateElementProperty(propertyKey, JSON.stringify(grad));
                      }}">Remove</button>
                    ` : nothing}
                  </div>
                  <div style="display: flex; flex-direction: column; gap: 8px;">
                    <div style="display: flex; gap: 8px; align-items: center;">
                      <input type="color" .value="${hex}" @input="${(e: any) => {
                        const rgba = this._hexToRgba(e.target.value, alpha);
                        stop.color = rgba;
                        this._updateElementProperty(propertyKey, JSON.stringify(grad));
                      }}" style="width: 24px; height: 24px; border-radius: var(--border-radius-sm); border: 1px solid var(--panel-border); cursor: pointer; padding: 0;" />
                      <input type="range" style="flex: 1;" min="0" max="1" step="0.01" .value="${stop.offset}" @input="${(e: any) => {
                        stop.offset = parseFloat(e.target.value);
                        this._updateElementProperty(propertyKey, JSON.stringify(grad));
                      }}" />
                    </div>
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                      <label style="font-size: 10px;">Opacity (${Math.round(alpha * 100)}%)</label>
                      <input type="range" style="width: 120px;" min="0" max="1" step="0.05" .value="${alpha}" @input="${(e: any) => {
                        const newRgba = this._hexToRgba(hex, parseFloat(e.target.value));
                        stop.color = newRgba;
                        this._updateElementProperty(propertyKey, JSON.stringify(grad));
                      }}" />
                    </div>
                  </div>
                </div>
              `;
            })}

            <button class="btn-action primary" style="width: 100%; margin-top: 10px; font-size: 11px; border-radius: var(--border-radius-md);" @click="${() => {
              const lastStop = grad.stops[grad.stops.length - 1];
              grad.stops.push({
                offset: 1.0,
                color: lastStop ? lastStop.color : 'rgba(239, 83, 80, 1)'
              });
              const n = grad.stops.length;
              grad.stops.forEach((stop: any, idx: number) => {
                stop.offset = idx / (n - 1);
              });
              this._updateElementProperty(propertyKey, JSON.stringify(grad));
            }}">+ Add Color Stop</button>
          </div>
        `}
      </div>
    `;
  }

  private _renderStrokeSection(el: any, colors: string[]) {
    const strokeWidth = el.strokeWidth || 2;
    const strokeStyle = el.strokeStyle || StrokeStyle.Solid;
    const strokeColor = el.strokeColor || '#000000';

    return html`
      <div class="panel-section">
        <div class="section-title">Border</div>
        <div class="property-grid" style="margin-bottom: 12px;">
          <div class="property-item full-width">
            <label>Border Color</label>
            <div class="input-wrapper" style="display: flex; gap: 8px; align-items: center;">
              <input type="color" .value="${strokeColor}" @input="${(e: any) => this._updateElementProperty('strokeColor', e.target.value)}" style="width: 32px; height: 32px; border-radius: var(--border-radius-md); border: 1px solid var(--panel-border); cursor: pointer; padding: 0;" />
              <input type="text" class="input-field" style="padding-left: 10px; height: 32px;" .value="${strokeColor}" @input="${(e: any) => this._updateElementProperty('strokeColor', e.target.value)}" />
            </div>
          </div>
          <div class="property-item">
            <label>Width</label>
            <input type="number" class="input-field" style="padding-left: 10px;" .value="${strokeWidth}" @input="${(e: any) => this._updateElementProperty('strokeWidth', parseFloat(e.target.value) || 1)}" />
          </div>
          <div class="property-item">
            <label>Style</label>
            <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${strokeStyle}" @change="${(e: any) => this._updateElementProperty('strokeStyle', e.target.value)}">
              <option value="solid">Solid</option>
              <option value="dash">Dashed</option>
              <option value="none">None</option>
            </select>
          </div>
        </div>
         <div class="color-swatch-list">
          <div 
            class="color-swatch ${strokeColor === 'transparent' ? 'active' : ''}" 
            style="background: linear-gradient(45deg, transparent 45%, #ff3b30 45%, #ff3b30 55%, transparent 55%), white; border: 1px solid var(--panel-border); border-radius: var(--border-radius-sm);"
            title="None"
            @click="${() => {
              this._updateElementProperty('strokeColor', 'transparent');
              this._updateElementProperty('strokeStyle', 'none');
            }}"
          ></div>
          ${colors.map(color => html`
            <div 
              class="color-swatch ${el.strokeColor === color ? 'active' : ''}" 
              style="background-color: ${color};"
              @click="${() => {
                this._updateElementProperty('strokeColor', color);
                if (el.strokeStyle === 'none') {
                  this._updateElementProperty('strokeStyle', 'solid');
                }
              }}"
            ></div>
          `)}
        </div>
      </div>
    `;
  }

  private _renderShapeSection(el: any, colors: string[]) {
    return html`
      ${this._renderPositionSection(el)}
      ${this._renderLayoutSection(el)}
      ${this._renderAppearanceSection(el)}
      ${this._renderColorOrGradientSection(el, 'fillColor', 'Fill Style', colors)}
      ${this._renderStrokeSection(el, colors)}
    `;
  }

  private _renderTextSection(el: any, colors: string[]) {
    return html`
      ${this._renderPositionSection(el)}
      ${this._renderLayoutSection(el)}
      
      <div class="panel-section">
        <div class="section-title">Typography</div>
        <div class="property-grid">
          <div class="property-item full-width">
            <label>Text Content</label>
            <textarea class="input-field" style="padding-left: 10px; resize: vertical;" rows="3" .value="${el.text || ''}" @input="${(e: any) => this._updateElementProperty('text', e.target.value)}"></textarea>
          </div>
          
          <div class="property-item">
            <label>Font Size</label>
            <input type="number" class="input-field" style="padding-left: 10px;" .value="${el.fontSize || 16}" @input="${(e: any) => this._updateElementProperty('fontSize', parseInt(e.target.value) || 16)}" />
          </div>
          
          <div class="property-item">
            <label>Text Align</label>
            <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${el.textAlign || 'left'}" @change="${(e: any) => this._updateElementProperty('textAlign', e.target.value)}">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
              <option value="justify">Justify</option>
            </select>
          </div>

          <div class="property-item">
            <label>Font Family</label>
            <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${el.fontFamily || 'Sans-Serif'}" @change="${(e: any) => this._updateElementProperty('fontFamily', e.target.value)}">
              <option value="Sans-Serif">Sans-Serif</option>
              <option value="Serif">Serif</option>
              <option value="Monospace">Monospace</option>
              <option value="Inter">Inter</option>
              <option value="Georgia">Georgia</option>
              <option value="system-ui">System</option>
            </select>
          </div>

          <div class="property-item">
            <label>Font Style</label>
            <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${el.fontStyle || 'normal'}" @change="${(e: any) => this._updateElementProperty('fontStyle', e.target.value)}">
              <option value="normal">Normal</option>
              <option value="italic">Italic</option>
            </select>
          </div>

          <div class="property-item">
            <label>Font Weight</label>
            <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${el.fontWeight || 'normal'}" @change="${(e: any) => this._updateElementProperty('fontWeight', e.target.value)}">
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="500">Medium</option>
              <option value="300">Light</option>
            </select>
          </div>

          <div class="property-item">
            <label>Text Decoration</label>
            <select class="input-field" style="padding-left: 10px; border-radius: var(--border-radius-md);" .value="${el.textDecoration || 'none'}" @change="${(e: any) => this._updateElementProperty('textDecoration', e.target.value)}">
              <option value="none">None</option>
              <option value="underline">Underline</option>
              <option value="line-through">Line Through</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Expose rich Gradient / Color Options for Texts -->
      ${this._renderColorOrGradientSection(el, 'color', 'Text Color', colors)}
      
      <!-- Opacity and Corner Radius for Text blocks -->
      ${this._renderAppearanceSection(el)}
    `;
  }

  private _renderImageSection(el: any) {
    const hue = el.hue || 0;
    const grayscale = el.grayscale || 0;
    const blur = el.blur || 0;
    const brightness = el.brightness || 100;
    const contrast = el.contrast || 100;

    return html`
      ${this._renderPositionSection(el)}
      ${this._renderLayoutSection(el)}
      ${this._renderAppearanceSection(el)}

      <div class="panel-section">
        <div class="section-title">Image filters</div>
        <div class="property-grid">
          <div class="property-item full-width">
            <label>Hue rotate (${hue}°)</label>
            <input type="range" min="0" max="360" .value="${hue}" @input="${(e: any) => this._updateElementProperty('hue', parseInt(e.target.value))}" />
          </div>
          <div class="property-item full-width">
            <label>Blur (${blur}px)</label>
            <input type="range" min="0" max="20" .value="${blur}" @input="${(e: any) => this._updateElementProperty('blur', parseInt(e.target.value))}" />
          </div>
          <div class="property-item full-width">
            <label>Grayscale (${grayscale}%)</label>
            <input type="range" min="0" max="100" .value="${grayscale}" @input="${(e: any) => this._updateElementProperty('grayscale', parseInt(e.target.value))}" />
          </div>
          <div class="property-item">
            <label>Brightness</label>
            <input type="number" class="input-field" style="padding-left: 10px;" .value="${brightness}" @input="${(e: any) => this._updateElementProperty('brightness', parseInt(e.target.value) || 100)}" />
          </div>
          <div class="property-item">
            <label>Contrast</label>
            <input type="number" class="input-field" style="padding-left: 10px;" .value="${contrast}" @input="${(e: any) => this._updateElementProperty('contrast', parseInt(e.target.value) || 100)}" />
          </div>
        </div>
        <div class="btn-group">
          <button class="btn-action" @click="${() => alert('Zoom to fit.')}">Zoom levels</button>
          <button class="btn-action primary" @click="${() => alert('Background Removal Successful.')}">Remove Background</button>
        </div>
      </div>
    `;
  }

  private _renderConnectorSection(el: any, colors: string[]) {
    return html`
      ${this._renderAppearanceSection(el)}
      ${this._renderStrokeSection(el, colors)}
    `;
  }

  private _renderBrushSection(el: any, colors: string[]) {
    const opacity = el.opacity !== undefined ? Math.round(el.opacity * 100) : 100;
    const color = el.color || '#000000';

    return html`
      ${this._renderPositionSection(el)}
      ${this._renderLayoutSection(el)}
      
      <!-- Brush Appearance (Opacity only, no corner radius) -->
      <div class="panel-section">
        <div class="section-title">Appearance</div>
        <div class="property-grid">
          <div class="property-item">
            <label>Opacity</label>
            <div class="input-wrapper">
              <input type="number" class="input-field" style="padding-left: 10px;" min="0" max="100" .value="${opacity}" @input="${(e: any) => {
                this._updateElementProperty('opacity', parseFloat(e.target.value) / 100);
              }}" />
            </div>
          </div>
        </div>
      </div>

      <!-- Brush Stroke Color (Solid color picker) -->
      <div class="panel-section">
        <div class="section-title">Brush Color</div>
        <div class="property-grid" style="margin-bottom: 12px;">
          <div class="property-item full-width">
            <div class="input-wrapper" style="display: flex; gap: 8px; align-items: center;">
              <input type="color" .value="${color}" @input="${(e: any) => this._updateElementProperty('color', e.target.value)}" style="width: 32px; height: 32px; border-radius: var(--border-radius-md); border: 1px solid var(--panel-border); cursor: pointer; padding: 0;" />
              <input type="text" class="input-field" style="padding-left: 10px; height: 32px;" .value="${color}" @input="${(e: any) => this._updateElementProperty('color', e.target.value)}" />
            </div>
          </div>
        </div>
        <div class="color-swatch-list">
          ${colors.map(c => html`
            <div 
              class="color-swatch ${color === c ? 'active' : ''}" 
              style="background-color: ${c};"
              @click="${() => this._updateElementProperty('color', c)}"
            ></div>
          `)}
        </div>
      </div>
    `;
  }

  private _renderDesignTab() {
    if (this._selectedElements.length === 0) {
      return html`
        <div class="empty-state">
          Select elements on the canvas to edit their design properties
        </div>
      `;
    }

    const firstEl = this._selectedElements[0];
    const isMulti = this._selectedElements.length > 1;

    // Detect element types
    const allShapes = this._selectedElements.every(el => el.shapeType || el.flavour === 'affine:shape');
    const isShape = firstEl.shapeType || firstEl.flavour === 'affine:shape';
    const isText = firstEl.text !== undefined || firstEl.flavour === 'affine:text' || firstEl.type === 'text';
    const isImage = firstEl.flavour === 'affine:image' || firstEl.flavour === 'affine:attachment' || firstEl.type === 'image';
    const isConnector = firstEl.flavour === 'affine:connector' || firstEl.flavour === 'affine:line' || firstEl.type === 'connector' || firstEl.type === 'line';
    const isBrush = firstEl.flavour === 'affine:brush' || firstEl.type === 'brush' || firstEl.flavour === 'brush';

    const colors = [
      '#ef5350', '#ec407a', '#ab47bc', '#7e57c2',
      '#29b6f6', '#26a69a', '#9ccc65', '#ffca28',
      '#ffa726', '#8d6e63', '#78909c', '#37474f'
    ];

    const typeLabel = isMulti ? 'Multiple Elements' : 
                      isShape ? 'Shape Frame' : 
                      isText ? 'Text Block' : 
                      isImage ? 'Image Frame' : 
                      isConnector ? 'Line Connector' : 
                      isBrush ? 'Hand-Drawn Stroke' :
                      'Canvas Block';

    return html`
      <!-- Section: Selection Title -->
      <div class="panel-section">
        <div class="frame-row">
          <select class="frame-select" style="border-radius: var(--border-radius-md);">
            <option>${typeLabel}</option>
          </select>
          <div class="frame-icons">
            <button class="frame-icon-btn" title="View Code">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6"/></svg>
            </button>
            <button class="frame-icon-btn" title="Inspect Layout">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="2 2"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Section: Alignment -->
      ${isMulti ? html`
        <div class="panel-section">
          <div class="section-title">Alignment</div>
          <div class="alignment-bar" style="border-radius: var(--border-radius-md);">
            <button @click="${() => this._handleAlign('left')}" class="alignment-btn" title="Align Left">
              <svg class="align-icon" viewBox="0 0 24 24"><path d="M4 2v20h2V2H4zm4 3v4h12V5H8zm0 14h8v-4H8v4zm0-5h14v-4H8v4z"/></svg>
            </button>
            <button @click="${() => this._handleAlign('center')}" class="alignment-btn" title="Align Horizontal Center">
              <svg class="align-icon" viewBox="0 0 24 24"><path d="M11 2h2v3h8v4h-8v6h5v4h-5v3h-2v-3H6v-4h5v-6H3V5h8V2z"/></svg>
            </button>
            <button @click="${() => this._handleAlign('right')}" class="alignment-btn" title="Align Right">
              <svg class="align-icon" viewBox="0 0 24 24"><path d="M20 2v20h-2V2h2zM4 9h12V5H4v4zm4 10h8v-4H8v4zm-4-5h12v-4H4v4z"/></svg>
            </button>
            <button @click="${() => this._handleAlign('top')}" class="alignment-btn" title="Align Top">
              <svg class="align-icon" viewBox="0 0 24 24"><path d="M2 4h20v2H2V4zm3 4v12h4V8H5zm14 0h-4v8h4V8zm-9 0v14h4V8h-4z"/></svg>
            </button>
            <button @click="${() => this._handleAlign('middle')}" class="alignment-btn" title="Align Vertical Center">
              <svg class="align-icon" viewBox="0 0 24 24"><path d="M2 11h3V3h4v8h6V6h4v5h3v2h-3v8h-4v-8h-6v5H5v-5H2v-2z"/></svg>
            </button>
            <button @click="${() => this._handleAlign('bottom')}" class="alignment-btn" title="Align Bottom">
              <svg class="align-icon" viewBox="0 0 24 24"><path d="M2 20h20v2H2v-2zm3-3h4V5H5v12zm14 0h-4V9h4v8zm-9 0h4V3h-4v14z"/></svg>
            </button>
          </div>
        </div>
      ` : nothing}

      <!-- Dynamic properties per type -->
      ${isMulti ? html`
        ${this._renderPositionSection(firstEl)}
        ${this._renderLayoutSection(firstEl)}
        ${this._renderAppearanceSection(firstEl)}
      ` : html`
        ${isShape ? this._renderShapeSection(firstEl, colors) : nothing}
        ${isText ? this._renderTextSection(firstEl, colors) : nothing}
        ${isImage ? this._renderImageSection(firstEl) : nothing}
        ${isConnector ? this._renderConnectorSection(firstEl, colors) : nothing}
        ${isBrush ? this._renderBrushSection(firstEl, colors) : nothing}
        ${(!isShape && !isText && !isImage && !isConnector && !isBrush) ? html`
          ${this._renderPositionSection(firstEl)}
          ${this._renderLayoutSection(firstEl)}
          ${this._renderAppearanceSection(firstEl)}
        ` : nothing}
      `}
    `;
  }

  override render() {
    return html`
      <div class="properties-wrapper">
        <div class="panel-header">
          <div class="panel-title">Properties</div>
          <button class="panel-close-btn" @click="${() => this.toggle(false)}" title="Close Panel">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        ${this._renderDesignTab()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'properties-panel': PropertiesPanel;
  }
}
